'use client';

/**
 * useRealtimeManagerChannels
 *
 * Subscribes to three Supabase realtime channels and broadcasts live events
 * to all connected manager sessions:
 *
 *  1. duty_assignments  → duty assignment changes (INSERT / UPDATE / DELETE)
 *  2. crew_members      → crew status updates (INSERT / UPDATE / DELETE)
 *  3. manager_conflict_alerts (broadcast) → conflict alerts pushed by any client
 *
 * Usage:
 *   const { conflictAlerts, clearConflict } = useRealtimeManagerChannels({ onRefresh });
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConflictSeverity = 'critical' | 'warning' | 'info';

export interface ConflictAlert {
  id: string;
  severity: ConflictSeverity;
  title: string;
  detail: string;
  dutyCode?: string;
  crewName?: string;
  route?: string;
  timestamp: Date;
  isNew: boolean;
}

interface UseRealtimeManagerChannelsOptions {
  /** Called after any DB change so the host component can re-fetch its data */
  onRefresh?: () => void;
  /** If false the hook is a no-op (useful for conditional mounting) */
  enabled?: boolean;
}

interface UseRealtimeManagerChannelsReturn {
  /** Live conflict alerts accumulated since mount */
  conflictAlerts: ConflictAlert[];
  /** Remove a single alert by id */
  clearConflict: (id: string) => void;
  /** Clear all alerts */
  clearAllConflicts: () => void;
  /** Broadcast a conflict alert to all connected managers */
  broadcastConflict: (alert: Omit<ConflictAlert, 'id' | 'timestamp' | 'isNew'>) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildDutyMessage(
  eventType: string,
  row: Record<string, any>
): { message: string; type: 'success' | 'info' | 'warning' | 'error' } {
  const dutyCode = row.duty_code ?? 'Duty';
  const route = row.route ?? '';
  const driver = row.driver1_name ?? '';
  const conductor = row.conductor_name ?? '';
  const status = row.status ?? '';

  if (eventType === 'INSERT') {
    const crew = [driver, conductor].filter((n) => n && n !== '—').join(' & ');
    return {
      message: `📋 ${dutyCode} assigned${route ? ` on ${route}` : ''}${crew ? ` — ${crew}` : ''}`,
      type: 'success',
    };
  }
  if (eventType === 'DELETE') {
    return { message: `🗑️ ${dutyCode} removed${route ? ` (${route})` : ''}`, type: 'warning' };
  }
  // UPDATE
  const icons: Record<string, string> = {
    Active: '🟢', Completed: '✅', Cancelled: '🚫', Delayed: '⚠️', Scheduled: '📅',
  };
  if (status) {
    const icon = icons[status] ?? '🔄';
    return {
      message: `${icon} ${dutyCode}${route ? ` (${route})` : ''} → ${status}`,
      type: status === 'Cancelled' ? 'error' : status === 'Delayed' ? 'warning' : 'info',
    };
  }
  const crew = [driver, conductor].filter((n) => n && n !== '—').join(' & ');
  return {
    message: `✏️ ${dutyCode}${route ? ` (${route})` : ''} updated${crew ? ` — ${crew}` : ''}`,
    type: 'info',
  };
}

function buildCrewMessage(
  eventType: string,
  newRow: Record<string, any>,
  oldRow: Record<string, any>
): { message: string; type: 'success' | 'info' | 'warning' | 'error' } {
  const row = newRow ?? oldRow ?? {};
  const name = row.name ?? 'Crew member';
  const role = row.role ?? '';

  if (eventType === 'INSERT') {
    return { message: `👤 ${name}${role ? ` (${role})` : ''} added to crew`, type: 'success' };
  }
  if (eventType === 'DELETE') {
    return { message: `🗑️ ${name}${role ? ` (${role})` : ''} removed from crew`, type: 'warning' };
  }
  const prevStatus = oldRow?.status ?? '';
  const newStatus = newRow?.status ?? '';
  if (newStatus && newStatus !== prevStatus) {
    const icons: Record<string, string> = {
      'On Trip': '🚌', Available: '✅', Rest: '😴', Leave: '🏖️', Suspended: '🚫',
    };
    const icon = icons[newStatus] ?? '🔄';
    return {
      message: `${icon} ${name} → ${newStatus}`,
      type: newStatus === 'Suspended' ? 'error' : newStatus === 'Leave' ? 'warning' : 'info',
    };
  }
  return { message: `✏️ ${name}${role ? ` (${role})` : ''} profile updated`, type: 'info' };
}

function showToast(message: string, type: 'success' | 'info' | 'warning' | 'error') {
  const opts = { duration: 4500 };
  switch (type) {
    case 'success': toast.success(message, opts); break;
    case 'warning': toast.warning(message, opts); break;
    case 'error':   toast.error(message, opts);   break;
    default:        toast.info(message, opts);    break;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRealtimeManagerChannels({
  onRefresh,
  enabled = true,
}: UseRealtimeManagerChannelsOptions = {}): UseRealtimeManagerChannelsReturn {
  const [conflictAlerts, setConflictAlerts] = useState<ConflictAlert[]>([]);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  // ── Conflict helpers ──────────────────────────────────────────────────────
  const clearConflict = useCallback((id: string) => {
    setConflictAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAllConflicts = useCallback(() => {
    setConflictAlerts([]);
  }, []);

  const broadcastConflict = useCallback(
    (alert: Omit<ConflictAlert, 'id' | 'timestamp' | 'isNew'>) => {
      if (!enabled) return;
      const supabase = createClient();
      const payload: ConflictAlert = {
        ...alert,
        id: uid(),
        timestamp: new Date(),
        isNew: true,
      };
      // Broadcast to all managers on the shared channel
      supabase
        .channel('manager_conflict_alerts')
        .send({
          type: 'broadcast',
          event: 'conflict',
          payload,
        })
        .catch((err) => console.error('broadcastConflict error:', err));
    },
    [enabled]
  );

  // ── Subscriptions ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const supabase = createClient();

    // 1. Duty assignment changes
    const dutyChannel = supabase
      .channel('mgr_duty_assignments')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'duty_assignments' },
        (payload) => {
          const { message, type } = buildDutyMessage(
            payload.eventType,
            (payload.new as Record<string, any>) ?? {}
          );
          showToast(message, type);
          setTimeout(() => onRefreshRef.current?.(), 400);
        }
      )
      .subscribe();

    // 2. Crew status updates
    const crewChannel = supabase
      .channel('mgr_crew_members')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crew_members' },
        (payload) => {
          const { message, type } = buildCrewMessage(
            payload.eventType,
            (payload.new as Record<string, any>) ?? {},
            (payload.old as Record<string, any>) ?? {}
          );
          showToast(message, type);
          setTimeout(() => onRefreshRef.current?.(), 400);
        }
      )
      .subscribe();

    // 3. Conflict alerts broadcast channel (all managers receive)
    const conflictChannel = supabase
      .channel('manager_conflict_alerts')
      .on('broadcast', { event: 'conflict' }, ({ payload }) => {
        const alert: ConflictAlert = {
          ...(payload as ConflictAlert),
          timestamp: new Date((payload as ConflictAlert).timestamp),
          isNew: true,
        };
        setConflictAlerts((prev) => [alert, ...prev].slice(0, 50));

        // Show a toast for the conflict
        const icon =
          alert.severity === 'critical' ? '🚨' : alert.severity === 'warning' ? '⚠️' : 'ℹ️';
        showToast(`${icon} ${alert.title}: ${alert.detail}`, alert.severity === 'critical' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info');

        // Auto-mark as not-new after 8 s
        setTimeout(() => {
          setConflictAlerts((prev) =>
            prev.map((a) => (a.id === alert.id ? { ...a, isNew: false } : a))
          );
        }, 8000);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(dutyChannel);
      supabase.removeChannel(crewChannel);
      supabase.removeChannel(conflictChannel);
    };
  }, [enabled]);

  return { conflictAlerts, clearConflict, clearAllConflicts, broadcastConflict };
}
